import { isCanUseCallSettings } from 'util/getCallType';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RoomModeratorRaw, WebinarModeData } from 'api/types';
import { flushSync } from 'react-dom';
import { useUserSettingsContext } from 'contexts';
import { ERROR_TYPE, FEATURES, TOKEN_STORAGE_KEY, VCS_JOIN_POLICY } from 'consts';
import { getLogger, LOGGERS } from 'lib/logger';
import { appStore } from 'state/store/app/app';
import { callPolicyStore } from 'state/store';
import { selectIsAuthorized, selectRoomId } from 'state/selectors';
import { isFeatureOn } from 'useCase';
import { useRoomVirtualBackground } from 'features/VirtualBackground/utils/setupVirtualBackgrounds';
import { useModal } from 'useCase/modal/useModal';
import { raiseHandSound } from 'features/RaiseHand/classes/RaiseHandSound';
import { applyPersistedWebinarMediaLocks, applyWebinarModeFromJoinResponse } from 'useCase/webinar/webinarPrejoinLocks';
import { applyModeratorsFromJoinResponse } from 'useCase/moderation/utils/mapModeratorsFromJoin';
import {
    cancelWaitingForJoinIfNeeded,
    clearLivekitTokens,
    clearWaitingQueue,
    enterWaitingQueue,
    getStringField,
    getWaitingListId,
    hasRoomIsFullError,
    isCallNotStartedError,
    isHasWaitingListId,
    isJoinRejectedError,
    isStaleWaitingRoomError,
    lockJoinSession,
    markAdmittedToRoom,
    resolveEffectiveJoinPolicy,
    resolveJoinAttempt,
    storeLivekitTokens,
    syncPrejoinCallPolicyIfNeeded,
    unlockJoinSession,
} from '../utils';
import { useConnectToRoom } from './useConnectToRoom';
import { useWaitingListId } from './useWaitingListId';

const logger = getLogger(LOGGERS.WaitingRoom);

export type ConnectToRoomParams = {
    fromParticipantsLimitRetry?: boolean;
    pollOnly?: boolean;
};

export interface UsePerformRoomJoinOptions {
    getUserName: () => string;
    onEnterRoom: () => void;
    onParticipantsLimitError?: () => void;
    onCallNotStarted?: () => void;
    onUnsupportedP2pJoin?: () => void;
    prepareVideoDevice?: () => Promise<void>;
}

export const usePerformRoomJoin = ({
    getUserName,
    onEnterRoom,
    onParticipantsLimitError,
    onCallNotStarted,
    onUnsupportedP2pJoin,
    prepareVideoDevice,
}: UsePerformRoomJoinOptions) => {
    const { closeModal } = useModal();
    const { setAudioEnabled, setVideoEnabled } = useUserSettingsContext();
    const { prepareRoomBackground } = useRoomVirtualBackground();
    const { getTokenAndWssUrl, getCleanName } = useConnectToRoom();
    const sessionWaitingListId = useWaitingListId();

    const [isWaitingForApproval, setIsWaitingForApproval] = useState(() => isHasWaitingListId());
    const [isJoinRejected, setIsJoinRejected] = useState(false);
    const [isParticipantsLimitRetryLoading, setIsParticipantsLimitRetryLoading] = useState(false);

    const isConnectInProgressRef = useRef(false);
    const isWaitingForApprovalRef = useRef(isWaitingForApproval);
    const isParticipantsLimitEnabled = isFeatureOn(FEATURES.VCS_PARTICIPANTS_LIMIT);
    const isAuthorized = selectIsAuthorized();

    useEffect(() => {
        isWaitingForApprovalRef.current = isWaitingForApproval;
    }, [isWaitingForApproval]);

    // CALL_DATA / sessionStorage могут выставить waitingListId без join() - держим UI в sync.
    useEffect(() => {
        if (!sessionWaitingListId) {
            return;
        }
        if (!isWaitingForApprovalRef.current) {
            isWaitingForApprovalRef.current = true;
            setIsWaitingForApproval(true);
        }
        setIsJoinRejected(false);
    }, [sessionWaitingListId]);

    const enterWaitingState = useCallback((waitingListId: string, pollOnly: boolean) => {
        const currentWaitingListId = getWaitingListId();
        if (pollOnly && currentWaitingListId && currentWaitingListId !== waitingListId) {
            return;
        }
        enterWaitingQueue(waitingListId);
        if (!pollOnly && isCanUseCallSettings() && resolveEffectiveJoinPolicy() === VCS_JOIN_POLICY.ALL) {
            callPolicyStore.update({ joinPolicy: VCS_JOIN_POLICY.ALL_WITH_APPROVAL });
        }
        if (appStore.callInitialData?.accountSettings?.requiresWaitingRoomRejoin) {
            appStore.patchCallData({
                accountSettings: {
                    ...appStore.callInitialData.accountSettings,
                    requiresWaitingRoomRejoin: false,
                },
            });
        }
        if (!isWaitingForApprovalRef.current) {
            flushSync(() => {
                isWaitingForApprovalRef.current = true;
                setIsWaitingForApproval(true);
            });
        } else {
            isWaitingForApprovalRef.current = true;
        }
        setIsJoinRejected(false);
        if (!pollOnly) {
            setIsParticipantsLimitRetryLoading(false);
        }
    }, []);

    const handleConnectError = useCallback(
        (
            error: unknown,
            { isWaitingSession, pollOnly }: { isWaitingSession: boolean; pollOnly: boolean },
        ): { ok: true; waiting: true } | { ok: false } => {
            const errorMessage = getStringField(error, 'message');

            if (errorMessage === ERROR_TYPE.VCS_P2P_JOIN_BY_LINK_OPERATION_NOT_SUPPORTED) {
                onUnsupportedP2pJoin?.();
                return { ok: false as const };
            }

            if (isParticipantsLimitEnabled && hasRoomIsFullError(error)) {
                onParticipantsLimitError?.();
                return { ok: false as const };
            }

            if (!pollOnly && !isWaitingSession && isCallNotStartedError(error)) {
                onCallNotStarted?.();
                return { ok: false as const };
            }

            if (error === 'WAITING_ROOM_REAPPROVAL_REQUIRED') {
                if (!isWaitingForApprovalRef.current) {
                    isWaitingForApprovalRef.current = true;
                    setIsWaitingForApproval(true);
                }
                setIsJoinRejected(false);
                if (!pollOnly) {
                    setIsParticipantsLimitRetryLoading(false);
                }
                return { ok: true as const, waiting: true as const };
            }

            if (isWaitingSession && (error === 'JOIN_REJECTED' || isJoinRejectedError(error))) {
                // flushSync: setIsJoinRejected до clearWaitingQueue, иначе useSyncExternalStore
                // (useWaitingListId) перерендерит EmbeddedPreJoin с isJoinRejected=false идем в PreJoin.
                flushSync(() => {
                    setIsJoinRejected(true);
                    setIsWaitingForApproval(false);
                });
                isWaitingForApprovalRef.current = false;
                clearWaitingQueue();
                clearLivekitTokens();
                appStore.patchCallData({ waitingListId: undefined });
                return { ok: false as const };
            }

            logger.error(error);
            return { ok: false as const };
        },
        [
            isParticipantsLimitEnabled,
            onUnsupportedP2pJoin,
            onParticipantsLimitError,
            onCallNotStarted,
            setIsWaitingForApproval,
            setIsJoinRejected,
            setIsParticipantsLimitRetryLoading,
        ],
    );

    const admitToRoom = useCallback(
        async (jwtToken: string, wssUrl: string, webinarMode?: WebinarModeData, moderators?: RoomModeratorRaw[]) => {
            storeLivekitTokens({ jwtToken, wssUrl });
            applyWebinarModeFromJoinResponse(webinarMode, selectRoomId(), setAudioEnabled, setVideoEnabled);
            applyModeratorsFromJoinResponse(moderators);
            await syncPrejoinCallPolicyIfNeeded(jwtToken);
            await prepareRoomBackground();
            applyPersistedWebinarMediaLocks(setAudioEnabled, setVideoEnabled, selectRoomId());
            markAdmittedToRoom();
            setIsParticipantsLimitRetryLoading(false);
            setIsWaitingForApproval(false);
            isWaitingForApprovalRef.current = false;
            setIsJoinRejected(false);
            closeModal();
            onEnterRoom();
        },
        [closeModal, onEnterRoom, prepareRoomBackground, setAudioEnabled, setVideoEnabled],
    );

    const connectToRoom = useCallback(
        async (params?: ConnectToRoomParams) => {
            if (isConnectInProgressRef.current) return;

            isConnectInProgressRef.current = true;
            try {
                const fromParticipantsLimitRetry = Boolean(params?.fromParticipantsLimitRetry);
                const pollOnly = Boolean(params?.pollOnly);
                const cleanName = getCleanName(getUserName());
                const roomId = selectRoomId();

                if (!pollOnly) {
                    closeModal();
                    setIsParticipantsLimitRetryLoading(fromParticipantsLimitRetry);

                    if (!cleanName) {
                        setIsParticipantsLimitRetryLoading(false);
                        return { ok: false as const, reason: 'empty_name' as const };
                    }

                    await prepareVideoDevice?.();
                    raiseHandSound.loadSound();
                }

                if (!isAuthorized) {
                    clearLivekitTokens();
                }

                const plan = resolveJoinAttempt({
                    pollOnly,
                    cleanName,
                    roomId,
                    isWaitingForApproval: isWaitingForApprovalRef.current || isHasWaitingListId(),
                });

                switch (plan.kind) {
                    case 'empty_name':
                        return { ok: false as const, reason: 'empty_name' as const };

                    case 'poll_skip_no_id':
                        if (!pollOnly) {
                            logger.warn(
                                '[waiting-room] waiting UI without waitingListId — skip join to avoid duplicate queue entry',
                            );
                        }
                        if (!pollOnly && !isHasWaitingListId() && !isJoinRejected) {
                            isWaitingForApprovalRef.current = false;
                            setIsWaitingForApproval(false);
                        }
                        return { ok: true as const, waiting: true as const };

                    case 'fast_reconnect':
                        await syncPrejoinCallPolicyIfNeeded(sessionStorage.getItem(TOKEN_STORAGE_KEY));
                        await prepareRoomBackground();
                        applyPersistedWebinarMediaLocks(setAudioEnabled, setVideoEnabled, roomId);
                        markAdmittedToRoom();
                        setIsParticipantsLimitRetryLoading(false);
                        setIsWaitingForApproval(false);
                        isWaitingForApprovalRef.current = false;
                        setIsJoinRejected(false);
                        closeModal();
                        onEnterRoom();
                        return { ok: true as const };

                    case 'api_join':
                        if (!plan.waitingListId && !isHasWaitingListId() && !isWaitingForApprovalRef.current) {
                            clearWaitingQueue();
                        } else if (plan.waitingListId && !isWaitingForApprovalRef.current) {
                            isWaitingForApprovalRef.current = true;
                            setIsWaitingForApproval(true);
                        }
                        break;
                }

                lockJoinSession();
                let result = await getTokenAndWssUrl(roomId, cleanName, plan.waitingListId).finally(unlockJoinSession);

                const isWaitingSession =
                    isWaitingForApprovalRef.current || isHasWaitingListId() || plan.waitingListId != null;

                if (result?.error && isStaleWaitingRoomError(result.error) && isWaitingSession) {
                    clearWaitingQueue();
                    appStore.patchCallData({ waitingListId: undefined });
                    // На poll не создаём новую заявку автоматически — иначе модератор approve по
                    // одному waitingListId, а веб уже поллит с другим (баннер мигает, accept не срабатывает).
                    if (pollOnly) {
                        flushSync(() => {
                            setIsWaitingForApproval(false);
                        });
                        isWaitingForApprovalRef.current = false;
                        return { ok: false as const };
                    }
                    lockJoinSession();
                    result = await getTokenAndWssUrl(roomId, cleanName, undefined).finally(unlockJoinSession);
                }

                const error = result?.error;
                const payload = result?.payload;

                if (payload && 'waitingListId' in payload && payload.waitingListId) {
                    applyWebinarModeFromJoinResponse(payload.webinarMode, roomId, setAudioEnabled, setVideoEnabled);
                    applyModeratorsFromJoinResponse(payload.moderators);
                    enterWaitingState(payload.waitingListId, pollOnly);
                    return { ok: true as const, waiting: true as const };
                }

                if (payload && 'jwtToken' in payload && payload.jwtToken && payload.wssUrl) {
                    await admitToRoom(payload.jwtToken, payload.wssUrl, payload.webinarMode, payload.moderators);
                    return { ok: true as const };
                }

                if (fromParticipantsLimitRetry) {
                    await new Promise<void>((resolve) => {
                        setTimeout(resolve, 300);
                    });
                }
                if (!pollOnly) {
                    setIsParticipantsLimitRetryLoading(false);
                }

                return handleConnectError(error, { isWaitingSession, pollOnly });
            } finally {
                isConnectInProgressRef.current = false;
            }
        },
        [
            getUserName,
            getCleanName,
            getTokenAndWssUrl,
            isAuthorized,
            onEnterRoom,
            prepareVideoDevice,
            admitToRoom,
            enterWaitingState,
            handleConnectError,
            closeModal,
            prepareRoomBackground,
        ],
    );

    const dismissJoinRejected = useCallback(() => {
        const waitingListId = getWaitingListId();
        const roomName = selectRoomId() || undefined;
        if (waitingListId && roomName) {
            void cancelWaitingForJoinIfNeeded({ waitingListId, roomName });
        }
        setIsJoinRejected(false);
        setIsWaitingForApproval(false);
        isWaitingForApprovalRef.current = false;
        clearWaitingQueue();
        clearLivekitTokens();
        appStore.patchCallData({ waitingListId: undefined });
    }, []);

    /**
     * Повторная попытка после reject. В отличие от dismissJoinRejected, оставляет
     * isWaitingForApproval=true пока идёт async reconnect — UI не мерцает на PreJoin/LoadingDots.
     */
    const retryAfterRejection = useCallback(async () => {
        const waitingListId = getWaitingListId();
        const roomName = selectRoomId() || undefined;
        if (waitingListId && roomName) {
            void cancelWaitingForJoinIfNeeded({ waitingListId, roomName });
        }
        clearWaitingQueue();
        clearLivekitTokens();
        setIsJoinRejected(false);
        // НЕ сбрасываем isWaitingForApproval — пусть UI остаётся на waiting page,
        // connectToRoom выставит новое значение через enterWaitingState/admitToRoom.
        isWaitingForApprovalRef.current = true;
        setIsWaitingForApproval(true);
        return connectToRoom();
    }, [connectToRoom]);

    return {
        isWaitingForApproval,
        isJoinRejected,
        isParticipantsLimitRetryLoading,
        connectToRoom,
        dismissJoinRejected,
        retryAfterRejection,
    };
};
